// packages/renderer/src/db/edb/folder-sync.ts
//
// Reading the connected folder and rebuilding the workspace list from it.
//
// Runs on connect and on an explicit Sync, never on boot: every file has to be
// read whole to learn what workspaces it holds, and a folder of 600k-row
// workspaces is not something to pay for on every page load.

import { normalizeWorkspaceDoc, type DataStore, type Dialogs, type WorkspaceContents } from '@easydb/shared';
import { edbBridge, storeBridge } from './active-bridge.js';
import { countWorkspaceContents } from '../delete-workspace.js';
import { fileInFolder, listWorkspaceFiles } from './file-handle.js';
import {
  activeFiles,
  folderConflicts,
  isEmptyWorkspace,
  partitionConflicts,
  readFolderIndex,
  readFolderSelection,
  workspaceLabel,
  writeFolderIndex,
  type FolderClash,
  type FolderIndex,
  type FolderWorkspace,
} from './folder-index.js';
import { afterRename, misfiledFiles, withoutFiles, type FileIdentity } from './file-identity.js';
import { changedHere, factsOf, factsOfHandle, readStamp, recordDivergence, verdictFor } from './file-stamp.js';
import { decideActiveFileSync, type ActiveFileOutcome } from './active-file-sync.js';
import { BROWSER_SIDE, compareCopies, fileSide, sizeChangeNote, type CopyFacts } from './copy-facts.js';
import { confirmDataLoss, newerSide, USE_BROWSER_COPY, USE_FILE_COPY } from './copy-choice.js';
import { COMPARE, NEWEST } from './merge-answers.js';
import { activeEdbName, adoptedFileName } from './session.js';
import { adoptFolderFile, reloadActiveFromFile } from './space-adopt.js';

/**
 * The two answers to one conflicting workspace. Compared by value.
 *
 * The SAME two strings the Open-workspace question and the Save clash use
 * (`copy-choice.ts`), because it is the same question: there are two copies and
 * one of them is going to win. They were "Load disk version" / "Overwrite disk
 * version" here and two other pairs elsewhere, so a user who had answered one of
 * these dialogs had learned nothing about the next.
 *
 * There is no explicit Cancel: `dialogs.choice` carries its own dismiss, a
 * dismissed dialog is neither of these, and neither branch runs — which is
 * exactly what Cancel meant.
 */
const LOAD = USE_FILE_COPY;
const OVERWRITE = USE_BROWSER_COPY;

/**
 * The third answer, for the one question where nothing is at stake.
 *
 * `file-newer` means the file moved and this copy holds nothing unsaved, so
 * declining costs nothing — there is no work here to write out and no reason to
 * offer {@link OVERWRITE}. Deliberately NOT the same string: this one keeps the
 * browser copy without writing it anywhere, and the two are never offered
 * together.
 */
const KEEP = 'Keep the browser copy';

/**
 * The two answers to a file whose name and contents disagree.
 *
 * "Leave it out" is not a repair and does not pretend to be one: the file stays on
 * disk exactly as it is, and the next sync asks again. It is the answer for a file
 * the user wants to look at themselves first.
 */
const renameTo = (id: string) => `Rename it to "${id}"`;
const LEAVE_OUT = 'Leave it out';

/**
 * Write this browser's copy of `workspaceId` into `file`, over the copy there.
 *
 * ONE id. It used to take two — the local one and the file's — because a clash
 * was matched by NAME and the two copies could then carry different ids, so the
 * file had to be cleared of each or it would come out holding both. A clash is
 * matched on the id itself now, so there is only ever one id in play.
 */
type OverwriteInFile = (workspaceId: string, file: string) => Promise<void>;

/** Give every workspace the folder does not hold a file of its own; names them. */
type FileTheRest = () => Promise<string[]>;

/**
 * Settle this tab's own file table by table, instead of picking one whole copy.
 *
 * Passed in rather than called directly, because it needs the worker bridge and
 * the workspace id — neither of which this module has, and both of which the
 * `edb-file` plugin already holds. Answers true when a merge actually ran; false
 * when the user backed out of it, which leaves both copies untouched.
 *
 * Absent by default, and then only Load and Overwrite are offered — which is what
 * every caller outside the browser build has.
 */
type MergeActiveFile = (handle: FileSystemFileHandle, mode: 'newest' | 'compare') => Promise<boolean>;

/**
 * Rename the workspace inside `file` from `from` to `to`, in the file itself.
 *
 * Answers false when the file could not be written, which is the ordinary case for
 * a folder whose permission has lapsed. The caller then leaves the file out, since
 * a file it cannot repair is one it must not act on either.
 */
type RenameInFile = (file: string, from: string, to: string) => Promise<boolean>;

export interface SyncReport {
  /** Files the sync READ — not what the folder holds. See {@link offFiles}. */
  files: number;
  /**
   * Files left alone because this device has them switched off in the Local
   * Data dialog.
   *
   * Reported rather than passed over in silence: a sync that says "2 workspaces
   * in 2 files" about a folder holding five looks like it lost three, and the
   * user cannot tell a skipped file from a broken one without being told.
   */
  offFiles: number;
  /** Workspaces found across every file, including this tab's own. */
  found: number;
  /** Conflicts the user was asked about. */
  conflicts: number;
  /** Files that held no readable workspace record. */
  unreadable: string[];
  /**
   * Files the sync WROTE, for workspaces the folder did not hold yet.
   *
   * The sync is two-way from v0.0.428: a workspace that lived only in this
   * browser gets a file, so connecting a folder leaves nothing behind. Named
   * rather than counted, for the same reason `alsoWroteNote` names them.
   */
  wrote: string[];
  /**
   * Files whose workspace was renamed to match the file name, at the user's word.
   *
   * See `file-identity.ts`. Named rather than counted, like `wrote`: the sync has
   * changed what is inside somebody's file, and the toast has to say which.
   */
  renamed: string[];
  /**
   * Files left out of the workspace list because their name denies what is inside.
   *
   * Nothing was written to them and nothing was deleted. They are simply not acted
   * on, so the selector cannot offer a workspace this app would open the wrong file
   * for.
   */
  ignored: string[];
  /**
   * Files that hold more than one workspace, which a `.edb` may not.
   *
   * Listed and usable, unlike {@link ignored} — nothing was written to them and
   * nothing was hidden. They are reported because the selector shows one row per
   * workspace and two rows out of one file read as the same thing, which is what
   * sent the user here. The repair is to open such a file and Save: that writes the
   * open workspace alone and gives every passenger a file of its own.
   */
  shared: string[];
  /** What happened to the file this tab has open. Never silent — see `active-file-sync.ts`. */
  active: ActiveFileOutcome;
  /** The name of that file, for the report to say. Absent on the local database. */
  activeFile?: string | undefined;
  /**
   * This tab's own file was written by something else and has been re-read.
   *
   * The tab reloads when this is true, so nothing after the sync gets a chance to
   * report it — it is here for the tests, which cannot see a reload.
   */
  reloadedActive?: boolean;
}

/**
 * Read the `.edb` files in `dir` this device uses, and record what workspaces
 * they hold.
 *
 * One file at a time rather than in parallel: the reads go through the single
 * worker, and holding several whole databases in its memory at once is the one
 * way this becomes a problem on a big folder.
 *
 * A file the user has switched off in the Local Data dialog is NOT read — not
 * parsed, not conflict-checked, never written. Its NAME is still collected, and
 * that is deliberate: listing a directory is cheap, and without the name there
 * would be nothing in the dialog to switch back on. Everything expensive is on
 * the far side of that check.
 *
 * `files` is every name found; `read` is the subset this scan actually opened.
 */
export async function scanFolder(dir: FileSystemDirectoryHandle): Promise<{ index: FolderIndex; files: string[]; read: string[]; unreadable: string[] }> {
  const bridge = edbBridge();
  const files = await listWorkspaceFiles(dir);
  const read = activeFiles(files, readFolderSelection(), activeEdbName());
  const workspaces: FolderWorkspace[] = [];
  const unreadable: string[] = [];

  for (const file of read) {
    const handle = await fileInFolder(dir, file, false);
    // The `File` object, not just the bytes: its size and last-modified time are
    // what the conflict prompts show about the copy the user cannot see, and they
    // come from the same read.
    const opened = handle ? await handle.getFile().catch(() => null) : null;
    const peeked = opened && bridge ? await bridge.peekWorkspaces(new Uint8Array(await opened.arrayBuffer())) : [];
    if (peeked.length === 0) {
      unreadable.push(file);
      continue;
    }
    for (const peek of peeked) {
      const { tables, views } = peek;
      // Through the same rule the store reads by. This path deserializes bytes
      // into a throwaway connection and queries `_easydb` directly, so nothing
      // else would turn an older file's `name` into the title it now serves as.
      const doc = normalizeWorkspaceDoc(peek.doc);
      const id = typeof doc['id'] === 'string' ? doc['id'] : '';
      if (!id) continue;
      workspaces.push({
        id,
        title: typeof doc['title'] === 'string' ? doc['title'] : undefined,
        file,
        tables,
        views,
        ...(opened ? factsOf(opened) : {}),
      });
    }
  }

  // `files` goes into the index too, so the Local Data dialog can list what the
  // folder holds — including the files this scan deliberately skipped — without
  // re-reading the directory.
  return { index: { folder: dir.name, at: Date.now(), workspaces, files }, files, read, unreadable };
}

/** One file, as a question about it should describe it. Always by name. */
function identityFacts(i: FileIdentity): string {
  return compareCopies([{ label: i.file, facts: { ...(i.size === undefined ? {} : { size: i.size }), ...(i.mtime === undefined ? {} : { mtime: i.mtime }) } }]);
}

/**
 * Settle every file in the folder whose name denies the workspace inside it.
 *
 * Runs on the scan result BEFORE the index is written and before any clash is
 * computed, because everything downstream reads a workspace id and a file name as
 * a matched pair. See `file-identity.ts` for what goes wrong when they are not.
 *
 * A rename is offered only where it would work, and a file the user does not repair
 * is dropped from the list rather than shown. Dropping is also what a failed write
 * comes to: a file this app cannot repair is one it must not act on either.
 *
 * The file this tab has OPEN is never touched. Its workspace is live — the store,
 * the panels and every plugin are bound to that id — so renaming it inside the file
 * would leave the tab saving under an id the file no longer holds. A tab looking at
 * a misfiled file is a state to leave alone until it looks somewhere else.
 */
async function settleIdentities(
  found: readonly FolderWorkspace[],
  activeFile: string,
  dialogs: Dialogs,
  rename: RenameInFile,
): Promise<{ workspaces: FolderWorkspace[]; renamed: string[]; ignored: string[]; shared: string[] }> {
  let workspaces = [...found];
  const renamed: string[] = [];
  const ignored: string[] = [];
  const shared: string[] = [];

  for (const bad of misfiledFiles(found)) {
    // A file holding several workspaces is named in the report and otherwise left
    // exactly as it is — including the one this tab has open, which is the case the
    // user can act on. Not a dialog: the file still works, every passenger in it is
    // reachable, and a modal on every sync about a state the user may be living with
    // on purpose is the kind of warning people learn to click away. Not dropped
    // either, which is what the other two cases come to: dropping it would take real
    // workspaces out of the list and the app cannot put them anywhere else.
    if (bad.fix === 'shared') {
      shared.push(bad.file);
      continue;
    }

    if (bad.file.toLowerCase() === activeFile.toLowerCase()) continue;

    if (bad.fix === 'ambiguous') {
      // Nothing to choose: the file name already claims the id it would be renamed
      // to, and so does another file. Only a rename on disk settles it.
      await dialogs.alert(
        `${bad.file} and ${bad.rivals.join(', ')} all stand for the workspace "${bad.claimed}". Only one file can. ${bad.file} is left out of the workspace list until you rename it on disk.${identityFacts(bad)}`,
        'Sync workspace folder',
      );
      ignored.push(bad.file);
      workspaces = withoutFiles(workspaces, [bad.file]);
      continue;
    }

    const answer = await dialogs.choice(
      `${bad.file} holds the workspace "${bad.id}". A workspace file must hold the workspace its own name says, so this copy cannot be opened. Rename the workspace inside it to "${bad.claimed}", or leave the file out of the list.${identityFacts(bad)}`,
      [renameTo(bad.claimed), LEAVE_OUT],
      'Sync workspace folder',
    );
    // A dismissed dialog leaves the file out, like the explicit answer: the app has
    // no safe use for it either way, and dropping it writes nothing.
    if (answer === renameTo(bad.claimed) && (await rename(bad.file, bad.id, bad.claimed))) {
      renamed.push(bad.file);
      workspaces = afterRename(workspaces, bad.file, bad.claimed);
    } else {
      ignored.push(bad.file);
      workspaces = withoutFiles(workspaces, [bad.file]);
    }
  }

  return { workspaces, renamed, ignored, shared };
}

/**
 * What each clashing workspace holds ON THIS SIDE.
 *
 * Only the clashing ones are counted — the folder can hold hundreds and this is
 * a question about the handful that appear twice. Rows are not counted: a table
 * is enough to make a workspace non-empty, and the row total is a `COUNT(*)` per
 * table.
 *
 * Serves two callers at once. `partitionConflicts` asks which side is an empty
 * shell, and the prompt asks what to SAY about the local copy, so counting once
 * answers both. A store that cannot count leaves the map empty: every clash then
 * gets its prompt, and the prompt says nothing about this side — the behaviour
 * before either existed.
 */
async function localContents(clashes: readonly FolderClash[]): Promise<Map<string, WorkspaceContents>> {
  const counts = new Map<string, WorkspaceContents>();
  try {
    const bridge = storeBridge();
    for (const clash of clashes) counts.set(clash.file.id, await countWorkspaceContents(bridge, clash.file.id, { countRows: false }));
  } catch {
    /* no bridge, or a build with no count — every clash gets its prompt */
  }
  return counts;
}

/** The ids whose local copy holds nothing the user put there. */
function emptyOnes(counts: ReadonlyMap<string, WorkspaceContents>): Set<string> {
  const empty = new Set<string>();
  for (const [id, c] of counts) if (isEmptyWorkspace(c)) empty.add(id);
  return empty;
}

/**
 * What a counted workspace looks like to the prompt. A file has a size; this side
 * has not.
 *
 * It has a DATE, though, and that is the one fact the browser line was missing
 * while the file line carried `saved 30 Sept 2026, 12:59`. It comes from this
 * tab's own file stamp — the open database is what the browser side IS — so it is
 * absent for a tab on the project index, which has no file to have moved away
 * from.
 */
function contentsAsFacts(c: WorkspaceContents | undefined): CopyFacts {
  return c ? { tables: c.tables, views: c.views } : {};
}

/** What the copy inside a file looks like to the prompt, as the scan recorded it. */
function fileAsFacts(w: FolderWorkspace): CopyFacts {
  return { tables: w.tables, views: w.views, size: w.size, mtime: w.mtime };
}

/**
 * Everything the OPEN database holds, for the question about this tab's own file.
 *
 * That question is about the whole file rather than one workspace, so the answer
 * is a total: this file was the source of every workspace in here. The count is
 * best-effort — a store that cannot count still gets the workspace number, which
 * it knows without asking.
 */
async function openFileFacts(open: readonly { id: string }[]): Promise<CopyFacts> {
  try {
    const bridge = storeBridge();
    let tables = 0;
    let views = 0;
    for (const w of open) {
      const c = await countWorkspaceContents(bridge, w.id, { countRows: false });
      tables += c.tables;
      views += c.views;
    }
    return { workspaces: open.length, tables, views };
  } catch {
    return { workspaces: open.length };
  }
}

/** The same totals for one FILE, out of the scan that just ran. */
function fileTotals(index: FolderIndex, file: string): CopyFacts {
  const mine = index.workspaces.filter((w) => w.file === file);
  if (mine.length === 0) return {};
  return {
    workspaces: mine.length,
    tables: mine.reduce((n, w) => n + (w.tables ?? 0), 0),
    views: mine.reduce((n, w) => n + (w.views ?? 0), 0),
  };
}

/** "It was 96 KB when this tab last read it", when the size moved. */
function sinceWeRead(file: string, nowSize: number | undefined): string {
  return sizeChangeNote(readStamp(file)?.size, nowSize);
}

/**
 * Scan `dir`, settle any conflicts with the open database, write out every
 * workspace the folder does not hold yet, and store the index.
 *
 * A conflict is one workspace NAME living both here and in a file. The user
 * answers per workspace, because the right answer is per workspace: a stale copy
 * on disk and a stale copy in the browser can both be in the same folder.
 *
 * Except when this side is empty. `?space=<name>` creates the workspace it cannot
 * find, and at boot it cannot look inside a folder nobody has connected yet, so
 * every private window that opens such a link has an empty shell of that name by
 * the time the folder arrives. Asking which of the two is real is a question
 * about nothing — the file is taken, unasked (`partitionConflicts`).
 *
 *  - **Use the file copy** adopts that file and reloads. The data does not move;
 *    the tab changes which database it is looking at, so everything else in that
 *    file comes with it. This returns only if the file has gone since the scan.
 *  - **Use the browser copy** writes the open workspace out over the file's copy
 *    of it, leaving the file's OTHER workspaces alone. Replacing the whole file
 *    would be the simpler read of "overwrite", and it would silently destroy
 *    workspaces the user never mentioned.
 *  - **Dismissing** leaves both. The index keeps the file's entry, so the selector
 *    shows the pair with the file name telling them apart.
 *
 * The index is written BEFORE the prompts, so a user who dismisses them still gets
 * the merged list they asked for.
 *
 * Ahead of all of it, a file whose name denies the workspace inside it is settled
 * or set aside (`settleIdentities`). That has to come first: a clash, an adopt and
 * a switch all read an id and a file name as one pair, so a file where the two
 * disagree makes every later step answer about the wrong copy.
 */
export async function syncFolder(
  dir: FileSystemDirectoryHandle,
  store: DataStore,
  dialogs: Dialogs,
  overwrite: OverwriteInFile,
  fileTheRest: FileTheRest = () => Promise.resolve([]),
  rename: RenameInFile = () => Promise.resolve(false),
  merge: MergeActiveFile | null = null,
): Promise<SyncReport> {
  const { index, files, read, unreadable } = await scanFolder(dir);

  // First, because everything below reads an id and a file name as a matched pair.
  const identity = await settleIdentities(index.workspaces, activeEdbName(), dialogs, rename);
  index.workspaces = identity.workspaces;

  writeFolderIndex(index);

  const open = await store.workspaces.find();
  const clashes = folderConflicts(open, index.workspaces, activeEdbName());
  const counted = await localContents(clashes);
  const { adopt, ask } = partitionConflicts(clashes, emptyOnes(counted));

  for (const clash of ask) {
    // The browser side IS this tab's own database, so its date is that file's
    // stamp. A tab on the project index has no file and no date, and the line
    // reads as it always did.
    const mine = { ...contentsAsFacts(counted.get(clash.file.id)), ...changedHere(activeEdbName()) };
    const theirs = fileAsFacts(clash.file);
    const sides = compareCopies([
      { label: BROWSER_SIDE, facts: mine },
      { label: fileSide(clash.file.file), facts: theirs },
    ]);
    const answer = await dialogs.choice(
      `"${workspaceLabel(clash.file)}" is in this browser and in ${clash.file.file}. The two may differ — which copy do you want to keep?${sides}`,
      [LOAD, OVERWRITE],
      'Sync workspace folder',
    );
    // A dismissed dialog keeps both, which is the safe answer and the only one
    // that touches nothing.
    if (answer === LOAD) {
      if (!(await confirmDataLoss(dialogs, workspaceLabel(clash.file), theirs, mine, 'the browser copy'))) continue;
      await adoptFolderFile(clash.file.id, clash.file.file);
    } else if (answer === OVERWRITE) {
      if (!(await confirmDataLoss(dialogs, workspaceLabel(clash.file), mine, theirs, `the file copy`))) continue;
      await overwrite(clash.file.id, clash.file.file);
    }
  }

  // Last, because an adopt reloads the tab: doing this first would carry the
  // prompts above away mid-question. Only the first one can take effect for the
  // same reason, and that is enough — after the reload the other empty shells sit
  // in a database this tab no longer has open, so they clash with nothing.
  for (const empty of adopt) await adoptFolderFile(empty.file.id, empty.file.file);

  // Everything the folder does NOT hold yet goes out to it, so a sync leaves no
  // workspace living only in this browser. After the prompts: a workspace the
  // user has just chosen the file copy of must not be written back out of the
  // browser copy they discarded.
  const wrote = await fileTheRest();

  const active = await refreshActiveFile(dir, dialogs, overwrite, open, index, merge);

  return {
    files: read.length,
    offFiles: files.length - read.length,
    found: index.workspaces.length,
    conflicts: ask.length,
    unreadable,
    wrote,
    renamed: identity.renamed,
    ignored: identity.ignored,
    shared: identity.shared,
    active,
    ...(adoptedFileName() === null ? {} : { activeFile: adoptedFileName() as string }),
    reloadedActive: active === 'loaded',
  };
}

/** What a single-workspace sync did, and to which file. */
export interface ActiveSyncReport {
  outcome: ActiveFileOutcome;
  /** The `.edb` this tab has adopted, or null when it is on browser storage. */
  file: string | null;
}

/**
 * Sync THIS workspace with its own `.edb`, and nothing else.
 *
 * The distinction from {@link syncFolder} is the whole reason this exists.
 * That one is about the folder: it reads every enabled `.edb` in it, settles
 * which file holds which workspace, writes the index, asks about every clash
 * between a file and a copy this browser holds, and files the workspaces that
 * live nowhere on disk. The file this tab has open is the LAST thing it
 * touches. Most of that work is about workspaces the user is not looking at,
 * and all of it needs every file in the folder opened and parsed.
 *
 * This is the one question a user means by "sync my workspace": has anyone
 * written MY file since I last read it, and what should happen about it. It
 * opens exactly one file — this tab's own — and asks nothing about any other.
 *
 * The rule it applies is `refreshActiveFile`'s, unchanged, so the two commands
 * cannot answer the same situation differently: `file-newer` is offered,
 * `conflict` and `unknown` are asked about with the four answers (Take newest,
 * Compare, Overwrite, Load), `same` and `ahead` do nothing and say why.
 *
 * The folder index is read from the last scan rather than rebuilt, because the
 * only thing it contributes is the table and view COUNTS shown beside each copy
 * in the dialog, and rebuilding it would mean opening every file — the cost this
 * command exists to avoid. A stale or absent index costs a line of description,
 * never a wrong decision: `fileTotals` yields nothing for a file it has not
 * seen, and the facts that decide anything — the file's size and mtime — are
 * read live from the handle every time.
 */
export async function syncActiveWorkspace(
  dir: FileSystemDirectoryHandle,
  store: DataStore,
  dialogs: Dialogs,
  overwrite: OverwriteInFile,
  merge: MergeActiveFile | null = null,
): Promise<ActiveSyncReport> {
  const open = await store.workspaces.find();
  const index = readFolderIndex() ?? { folder: dir.name, at: 0, workspaces: [] };
  const outcome = await refreshActiveFile(dir, dialogs, overwrite, open, index, merge);
  return { outcome, file: adoptedFileName() };
}

/**
 * Bring this tab's OWN file back in step with the copy on disk.
 *
 * The rest of a sync is about the workspace LIST — which file holds what — and it
 * skips the file this tab has open, because those workspaces are already here.
 * That left the one case a user means by "sync": three tabs on three origins, one
 * shared folder, and each tab looking at its own imported copy of the same `.edb`.
 * Everything except the folder is origin-scoped, so nothing told a tab that
 * another had written the file.
 *
 * Only for a file this tab ADOPTED. The project index is this browser's own and
 * is not a file at all, so a tab on `index.edp` has nothing in the folder that
 * could be a newer copy of it.
 *
 * **Nothing here happens quietly.** Every state the file can be in ends in a
 * question the user answers or a word the report says: an outside write is offered
 * rather than applied, and a pair this browser cannot compare (`unknown`) is asked
 * about rather than left. `unknown` used to return in silence, which is what a
 * Sync that appeared to do nothing was — and it is reached by ordinary use, since
 * an Overwrite once cleared the stamp that makes the comparison possible.
 */
async function refreshActiveFile(
  dir: FileSystemDirectoryHandle,
  dialogs: Dialogs,
  overwrite: OverwriteInFile,
  open: readonly { id: string }[],
  index: FolderIndex,
  merge: MergeActiveFile | null,
): Promise<ActiveFileOutcome> {
  const file = adoptedFileName();
  if (!file) return 'no-file';
  const handle = await fileInFolder(dir, file, false);
  const verdict = await verdictFor(file, handle);
  const plan = decideActiveFileSync(verdict, handle !== null);
  if (plan === 'nothing') {
    if (!handle) return 'missing';
    return verdict === 'ahead' ? 'ours-ahead' : 'in-step';
  }

  // What each side holds, for a question about the whole FILE rather than one
  // workspace — every workspace in this database came from those bytes.
  const now = handle ? await factsOfHandle(handle) : null;
  // The stamp already said which side moved, and it is the one thing the counts
  // cannot say. `ask-unknown` marks neither, which is the honest answer there.
  const newer = newerSide(verdict);
  const sides = compareCopies([
    { label: BROWSER_SIDE, facts: { ...(await openFileFacts(open)), ...changedHere(file) }, newer: newer === 'browser' },
    { label: fileSide(file), facts: { ...fileTotals(index, file), ...(now ?? {}) }, newer: newer === 'file' },
  ]);
  const since = sinceWeRead(file, now?.size);

  // The safe question: the file moved and this copy holds nothing unsaved, so the
  // only thing at stake is that the screen changes under the user.
  if (plan === 'ask-load') {
    const answer = await dialogs.choice(
      `${file} has been written since this tab last read it. Nothing here is unsaved, so this copy can be replaced by the one on disk.${sides}${since}`,
      [LOAD, KEEP],
      'Sync workspace folder',
    );
    if (answer !== LOAD) return 'kept';
    return (await reloadActiveFromFile(file)) ? 'loaded' : 'missing';
  }

  // Nothing to compare with: no stamp at all, so "the file is newer" and "we have
  // never read it" look the same. Both answers are on the table, because either
  // copy may be the real one and only the user knows which.
  const question =
    plan === 'ask-unknown'
      ? `This tab has ${file} open, but this browser has no record of when the two last agreed, so it cannot tell which is newer.${sides}`
      : `${file} has been written since this tab read it, and there are unsaved changes here.${sides}${since}`;

  // The four-answer question, where there is something to compare WITH. Both of
  // these states mean both copies may hold work, so "which one do you want to
  // keep" was always the wrong question — it just used to be the only one this
  // layer could act on.
  if (merge && handle) {
    const answer = await dialogs.choice(`${question}`, [NEWEST, COMPARE, OVERWRITE, LOAD], 'Sync workspace folder');
    if (answer === NEWEST || answer === COMPARE) return (await merge(handle, answer === NEWEST ? 'newest' : 'compare')) ? 'merged' : 'kept';
    if (answer === LOAD) return (await reloadActiveFromFile(file)) ? 'loaded' : 'missing';
    if (answer !== OVERWRITE) return 'kept';
    return overwriteWholeFile(file, handle, open, overwrite);
  }

  const answer = await dialogs.choice(`${question} Which copy do you want to keep?`, [LOAD, OVERWRITE], 'Sync workspace folder');
  if (answer === LOAD) return (await reloadActiveFromFile(file)) ? 'loaded' : 'missing';
  if (answer === OVERWRITE) return overwriteWholeFile(file, handle, open, overwrite);
  // Dismissed. Both copies stay exactly as they are, which is what Cancel meant.
  return 'kept';
}

/**
 * Write this tab's copy over the file — the blunt answer, kept for the two
 * callers above.
 *
 * Per workspace, not the whole file: the newer copy on disk may hold workspaces
 * this tab has never seen, and they are not ours to drop. Same id on both sides,
 * because this is THIS tab's own file and the copy inside it is the one this
 * workspace was written from.
 */
async function overwriteWholeFile(file: string, handle: FileSystemFileHandle | null, open: readonly { id: string }[], overwrite: OverwriteInFile): Promise<ActiveFileOutcome> {
  for (const w of open) await overwrite(w.id, file);
  // What the file looks like NOW, plus the note that this database is not a copy
  // of it — the file may hold workspaces we did not write. See `recordDivergence`.
  const written = handle ? await factsOfHandle(handle) : null;
  if (written) recordDivergence(file, written);
  return 'overwritten';
}
